import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'
import api from '../services/api'

interface DataType {
  id: string
  name: string
  description: string
  fields: string[]
}

interface ExportJob {
  id: string
  data_type: string
  export_format: string
  status: string
  progress: number
  created_at: string
  completed_at?: string
  row_count: number
  file_size: number
  download_url?: string
}

interface PreviewData {
  data_type: string
  preview: Record<string, unknown>[]
  total_count: number
  fields: string[]
}

const BulkExport: React.FC = () => {
  const [dataTypes, setDataTypes] = useState<DataType[]>([])
  const [exportJobs, setExportJobs] = useState<ExportJob[]>([])
  const [selectedDataType, setSelectedDataType] = useState<string>('')
  const [selectedFormat, setSelectedFormat] = useState<string>('csv')
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const formats = [
    { id: 'csv', name: 'CSV', icon: '📄', description: 'Comma-separated values, opens in Excel' },
    { id: 'json', name: 'JSON', icon: '📋', description: 'JavaScript Object Notation, for developers' },
    { id: 'xlsx', name: 'Excel', icon: '📊', description: 'Native Excel format' },
    { id: 'pdf', name: 'PDF', icon: '📕', description: 'Print-ready format' },
  ]

  useEffect(() => {
    loadDataTypes()
    loadExportJobs()
  }, [])

  useEffect(() => {
    if (selectedDataType) {
      loadPreview()
      const dataType = dataTypes.find(dt => dt.id === selectedDataType)
      if (dataType) {
        setSelectedFields(dataType.fields)
      }
    }
  }, [selectedDataType, dataTypes])

  const loadDataTypes = async () => {
    try {
      const response = await api.get('/api/bulk-operations/data-types')
      setDataTypes(response.data)
      if (response.data.length > 0) {
        setSelectedDataType(response.data[0].id)
      }
    } catch (error) {
      console.error('Failed to load data types:', error)
      // Use mock data
      const mockTypes: DataType[] = [
        { id: 'markets', name: 'Markets', description: 'Market performance data', fields: ['country', 'name', 'revenue', 'orders', 'sellers'] },
        { id: 'sellers', name: 'Sellers', description: 'Seller metrics', fields: ['id', 'name', 'status', 'monthly_revenue', 'total_orders'] },
        { id: 'products', name: 'Products', description: 'Product catalog', fields: ['id', 'name', 'sku', 'category', 'price'] },
        { id: 'orders', name: 'Orders', description: 'Order data', fields: ['id', 'order_number', 'customer', 'amount', 'status'] },
      ]
      setDataTypes(mockTypes)
      setSelectedDataType('markets')
    }
  }

  const loadExportJobs = async () => {
    try {
      const response = await api.get('/api/bulk-operations/export/user/jobs')
      setExportJobs(response.data)
    } catch (error) {
      console.error('Failed to load export jobs:', error)
    }
  }

  const loadPreview = async () => {
    if (!selectedDataType) return

    setLoading(true)
    try {
      const response = await api.get(`/api/bulk-operations/preview/${selectedDataType}`)
      setPreviewData(response.data)
    } catch (error) {
      console.error('Failed to load preview:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleField = (field: string) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    )
  }

  const selectAllFields = () => {
    const dataType = dataTypes.find(dt => dt.id === selectedDataType)
    if (dataType) {
      setSelectedFields(dataType.fields)
    }
  }

  const deselectAllFields = () => {
    setSelectedFields([])
  }

  const startExport = async () => {
    if (!selectedDataType || selectedFields.length === 0) return

    setExporting(true)
    try {
      const response = await api.post('/api/bulk-operations/export', {
        data_type: selectedDataType,
        export_format: selectedFormat,
        selected_fields: selectedFields,
        user_id: 'default_user'
      })

      setExportJobs([response.data, ...exportJobs])

      // If completed immediately, download
      if (response.data.status === 'completed' && response.data.download_url) {
        window.open(`http://192.168.0.98:8090${response.data.download_url}`, '_blank')
      }
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setExporting(false)
    }
  }

  const downloadExport = (job: ExportJob) => {
    if (job.download_url) {
      window.open(`http://192.168.0.98:8090${job.download_url}`, '_blank')
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed': return '#10B981'
      case 'processing': return '#F59E0B'
      case 'pending': return '#6B7280'
      case 'failed': return '#EF4444'
      case 'cancelled': return '#9CA3AF'
      default: return '#6B7280'
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Bulk Export Center
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>
            Export your data in various formats for analysis and reporting
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px' }}>
          {/* Main Content */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Data Type Selection */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                1. Select Data Type
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {dataTypes.map(dt => (
                  <button
                    key={dt.id}
                    onClick={() => setSelectedDataType(dt.id)}
                    style={{
                      padding: '16px',
                      border: selectedDataType === dt.id ? '2px solid #4A90E2' : '2px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: selectedDataType === dt.id ? '#EFF6FF' : 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{dt.name}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      {dt.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Format Selection */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                2. Select Export Format
              </h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {formats.map(format => (
                  <button
                    key={format.id}
                    onClick={() => setSelectedFormat(format.id)}
                    style={{
                      padding: '12px 20px',
                      border: selectedFormat === format.id ? '2px solid #4A90E2' : '2px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: selectedFormat === format.id ? '#EFF6FF' : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{format.icon}</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>{format.name}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>{format.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Field Selection */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600' }}>
                  3. Select Fields
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={selectAllFields}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAllFields}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {dataTypes.find(dt => dt.id === selectedDataType)?.fields.map(field => (
                  <label
                    key={field}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      border: selectedFields.includes(field) ? '2px solid #4A90E2' : '2px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: selectedFields.includes(field) ? '#EFF6FF' : 'white',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field)}
                      onChange={() => toggleField(field)}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      border: selectedFields.includes(field) ? 'none' : '2px solid #D1D5DB',
                      backgroundColor: selectedFields.includes(field) ? '#4A90E2' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '10px'
                    }}>
                      {selectedFields.includes(field) && '✓'}
                    </span>
                    {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                4. Preview Data
              </h3>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                  Loading preview...
                </div>
              ) : previewData ? (
                <>
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: '#6B7280' }}>
                    Estimated total rows: <strong>{previewData.total_count.toLocaleString()}</strong>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                          {selectedFields.map(field => (
                            <th
                              key={field}
                              style={{
                                textAlign: 'left',
                                padding: '10px',
                                fontSize: '12px',
                                color: '#6B7280',
                                fontWeight: '600'
                              }}
                            >
                              {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview.slice(0, 5).map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            {selectedFields.map(field => (
                              <td key={field} style={{ padding: '10px', fontSize: '13px' }}>
                                {String(row[field] || '-')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                  Select a data type to preview
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Export Summary */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                Export Summary
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px'
                }}>
                  <span style={{ color: '#6B7280' }}>Data Type:</span>
                  <span style={{ fontWeight: '500' }}>
                    {dataTypes.find(dt => dt.id === selectedDataType)?.name || '-'}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px'
                }}>
                  <span style={{ color: '#6B7280' }}>Format:</span>
                  <span style={{ fontWeight: '500' }}>
                    {formats.find(f => f.id === selectedFormat)?.name || '-'}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px'
                }}>
                  <span style={{ color: '#6B7280' }}>Fields:</span>
                  <span style={{ fontWeight: '500' }}>{selectedFields.length} selected</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px'
                }}>
                  <span style={{ color: '#6B7280' }}>Est. Rows:</span>
                  <span style={{ fontWeight: '500' }}>
                    {previewData?.total_count.toLocaleString() || '-'}
                  </span>
                </div>
              </div>

              <button
                onClick={startExport}
                disabled={exporting || selectedFields.length === 0}
                style={{
                  width: '100%',
                  padding: '14px',
                  marginTop: '20px',
                  backgroundColor: selectedFields.length > 0 ? '#4A90E2' : '#E5E7EB',
                  color: selectedFields.length > 0 ? 'white' : '#9CA3AF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: selectedFields.length > 0 ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {exporting ? 'Exporting...' : '⬇ Start Export'}
              </button>
            </div>

            {/* Recent Exports */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                Recent Exports
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {exportJobs.length > 0 ? (
                  exportJobs.slice(0, 5).map(job => (
                    <div
                      key={job.id}
                      style={{
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>
                          {job.data_type}.{job.export_format}
                        </span>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: getStatusColor(job.status) + '20',
                          color: getStatusColor(job.status),
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {job.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {job.row_count.toLocaleString()} rows • {formatFileSize(job.file_size)}
                      </div>
                      {job.status === 'completed' && job.download_url && (
                        <button
                          onClick={() => downloadExport(job)}
                          style={{
                            marginTop: '8px',
                            padding: '6px 12px',
                            backgroundColor: '#EFF6FF',
                            color: '#4A90E2',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            width: '100%'
                          }}
                        >
                          Download
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '13px' }}>
                    No exports yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkExport
